"use client";

import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Textarea } from "@/app/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { QuantitySlider } from "@/app/components/ui/quantity-slider";
import { QrCode, Download, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { CopyButton } from "@/app/components/ui/copy-button";

export default function QRCodeGeneratorTool() {
  const [text, setText] = useState("");
  const [size, setSize] = useState(200);
  const [errorCorrection, setErrorCorrection] = useState("M");
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const generateQRCode = () => {
    if (!text.trim()) {
      toast.error("Please enter text or URL to generate QR code");
      return;
    }

    const params = new URLSearchParams({
      data: text,
      size: String(size),
      ecc: errorCorrection,
    });

    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?${params.toString()}`;
    setQrCodeUrl(qrUrl);
    toast.success("QR code generated successfully!");
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    
    const link = document.createElement("a");
    link.href = qrCodeUrl;
    link.download = "qrcode.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("QR code downloaded!");
  };

  const clearAll = () => {
    setText("");
    setQrCodeUrl("");
    toast.info("All data cleared");
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code Generator
            </CardTitle>
            <CardDescription>
              Generate QR codes for URLs, text, or any data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="text">Text or URL</Label>
              <Textarea
                id="text"
                placeholder="Enter text, URL, or any data to encode..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-4">
              <QuantitySlider
                label="Size"
                value={size}
                min={100}
                max={500}
                step={50}
                onValueChange={setSize}
                valueSuffix="px"
                hint="100–500px"
              />

              <div className="space-y-2">
                <Label htmlFor="errorCorrection">Error Correction</Label>
                <Select value={errorCorrection} onValueChange={setErrorCorrection}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Low (7%)</SelectItem>
                    <SelectItem value="M">Medium (15%)</SelectItem>
                    <SelectItem value="Q">Quartile (25%)</SelectItem>
                    <SelectItem value="H">High (30%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button onClick={generateQRCode} disabled={!text.trim()}>
                Generate QR Code
              </Button>
              {text && (
                <CopyButton text={text} variant="outline" successMessage="Text copied!">
                  Copy Text
                </CopyButton>
              )}
              <Button onClick={clearAll} variant="outline">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Generated QR Code</CardTitle>
            <CardDescription>
              Your QR code will appear here
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {qrCodeUrl ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <img
                    src={qrCodeUrl}
                    alt="Generated QR Code"
                    className="border rounded-lg"
                  />
                </div>
                <Button onClick={downloadQRCode} className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Download QR Code
                </Button>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <QrCode className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Enter text and click "Generate QR Code" to see your QR code here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>QR Code Information</CardTitle>
          <CardDescription>
            Learn about QR code generation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">7-30%</div>
              <div className="text-sm text-muted-foreground">Error Correction</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">4,296</div>
              <div className="text-sm text-muted-foreground">Max Characters</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">ISO/IEC 18004</div>
              <div className="text-sm text-muted-foreground">Standard</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
